/**
 * Employment Intelligence — workforce intelligence platform.
 *
 * Phase 1 ships:
 *   Overview · Employee Database · Profiles · Upload (PDF Library)
 *   + placeholder shells for Former Employees, Team Analytics, Recruitment
 *   Intelligence, AI Insights, Comparisons, Reports, Settings.
 *
 * The sub-nav is the single source of truth; each tab is its own component
 * file so we can layer features in without ballooning this file.
 */

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  LayoutDashboard, Users, UserMinus, BarChart3, Target, Sparkles,
  GitCompare, FileText, FolderOpen, Settings, Search, Upload as UploadIcon,
} from "lucide-react";
import { Toaster, toast } from "sonner";

import { AppHeader } from "./AppHeader";
import EIOverview from "./EI/EIOverview";
import EIEmployees from "./EI/EIEmployees";
import EIProfile from "./EI/EIProfile";
import EIUpload from "./EI/EIUpload";
import EISettings from "./EI/EISettings";
import EIPlaceholder from "./EI/EIPlaceholder";
import EIFormerEmployees from "./EI/EIFormerEmployees";

import "./EI/EmploymentIntelligence.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: "overview",     label: "Overview",                Icon: LayoutDashboard },
  { key: "employees",    label: "Employee Database",       Icon: Users },
  { key: "former",       label: "Former Employees",        Icon: UserMinus },
  { key: "teams",        label: "Team Analytics",          Icon: BarChart3, placeholder: true,
    placeholderTitle: "Team Analytics",
    placeholderBlurb: "Department-level balance scores, leadership distribution and AI summaries." },
  { key: "recruitment",  label: "Recruitment Intelligence",Icon: Target, placeholder: true,
    placeholderTitle: "Recruitment Intelligence",
    placeholderBlurb: "Recommended DISC ranges by role, learnt from your top performers and retention data." },
  { key: "ai-insights",  label: "AI Insights",             Icon: Sparkles, placeholder: true,
    placeholderTitle: "AI Insights Engine",
    placeholderBlurb: "On-demand AI scans of the entire dataset — discoveries with confidence scores and supporting data." },
  { key: "compare",      label: "Comparisons",             Icon: GitCompare, placeholder: true,
    placeholderTitle: "Comparison Centre",
    placeholderBlurb: "Side-by-side DISC, behaviour, communication and leadership diffs across employees and departments." },
  { key: "reports",      label: "Reports",                 Icon: FileText, placeholder: true,
    placeholderTitle: "Reports",
    placeholderBlurb: "Executive summaries, retention reports, department reports — exportable as PDF / Excel / CSV." },
  { key: "upload",       label: "PDF Library",             Icon: FolderOpen },
  { key: "settings",     label: "Settings",                Icon: Settings },
];

export default function EmploymentIntelligence({ onBack }) {
  const [tab, setTab] = useState("overview");
  const [activeProfileId, setActiveProfileId] = useState(null);

  // Overview source data — re-fetched on Overview / Employees tab switches
  const [overview, setOverview] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/employment-intelligence/overview`);
        if (!cancelled) setOverview(res.data);
      } catch {
        /* swallow — empty state renders fine */
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  const openProfile = (profileId) => {
    setActiveProfileId(profileId);
    setTab("profile");
  };

  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  const tabMeta = useMemo(
    () => TABS.find((t) => t.key === tab) || TABS[0],
    [tab]
  );

  return (
    <div className="ei" data-testid="employment-intelligence">
      <AppHeader onBack={onBack} title="Employment Intelligence" />
      <Toaster position="top-center" richColors closeButton />

      {/* Hero strip — sits above the sub-nav */}
      <div className="ei-hero">
        <div>
          <span className="ei-eyebrow">Workforce Intelligence</span>
          <h1 className="ei-title">AI-powered people analytics for NicheCom.</h1>
          <p className="ei-blurb">
            Upload DISC reports, build a searchable workforce database, and let AI surface the retention,
            hiring and team-balance patterns hiding in your assessments.
          </p>
        </div>
        <div className="ei-hero-actions">
          <button
            className="ei-primary"
            onClick={() => setTab("upload")}
            data-testid="ei-cta-upload"
          >
            <UploadIcon size={15} /> Upload assessment
          </button>
        </div>
      </div>

      {/* Sub-navigation */}
      <nav className="ei-tabs" data-testid="ei-tabs">
        <div className="ei-tabs-inner">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              className={`ei-tab ${tab === key ? "is-active" : ""}`}
              onClick={() => { setTab(key); if (key !== "profile") setActiveProfileId(null); }}
              data-testid={`ei-tab-${key}`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </nav>

      {/* Tab content */}
      <main className="ei-main">
        {tab === "overview" && (
          <EIOverview overview={overview} onOpenProfile={openProfile} onSwitchTab={setTab} />
        )}
        {tab === "employees" && (
          <EIEmployees onOpenProfile={openProfile} />
        )}
        {tab === "former" && (
          <EIFormerEmployees onOpenProfile={openProfile} />
        )}
        {tab === "profile" && (
          <EIProfile
            profileId={activeProfileId}
            onBack={() => setTab("employees")}
            onRefresh={triggerRefresh}
          />
        )}
        {tab === "upload" && (
          <EIUpload onComplete={(profileId) => { triggerRefresh(); if (profileId) openProfile(profileId); }} />
        )}
        {tab === "settings" && <EISettings />}
        {tabMeta.placeholder && (
          <EIPlaceholder
            title={tabMeta.placeholderTitle}
            blurb={tabMeta.placeholderBlurb}
            Icon={tabMeta.Icon}
          />
        )}
      </main>
    </div>
  );
}

// Convenience re-export for sub-components
export const formatScore = (v) => (typeof v === "number" ? Math.round(v) : "—");
export const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};
export const DISC_COLOURS = {
  D: { fill: "#e11d48", soft: "rgba(225, 29, 72, 0.12)", label: "Dominance" },
  I: { fill: "#f59e0b", soft: "rgba(245, 158, 11, 0.14)", label: "Influence" },
  S: { fill: "#10b981", soft: "rgba(16, 185, 129, 0.12)", label: "Steadiness" },
  C: { fill: "#3b82f6", soft: "rgba(59, 130, 246, 0.12)", label: "Compliance" },
};
export { toast };
